import { useState } from 'react';
import type { List } from '@shared/types.ts';
import { createList, selectList } from '../store/state.ts';

interface Props {
  lists: List[];
  selectedId: string | null;
  onOpenSettings: () => void;
}

/**
 * Sélecteur de liste, toujours visible en haut de l'écran.
 *
 * Des onglets défilants plutôt qu'un menu déroulant : à deux ou trois listes,
 * un menu demanderait deux gestes là où un onglet en demande un.
 */
export default function ListSelector({ lists, selectedId, onOpenSettings }: Props) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');

  return (
    <div className="lists">
      <div className="lists__tabs" role="tablist" aria-label="Listes">
        {lists.map((list) => (
          <button
            key={list.id}
            type="button"
            role="tab"
            aria-selected={list.id === selectedId}
            className={`lists__tab${list.id === selectedId ? ' lists__tab--active' : ''}`}
            onClick={() => selectList(list.id)}
          >
            {list.name}
          </button>
        ))}

        <button
          type="button"
          className="lists__tab lists__tab--add"
          onClick={() => setCreating(true)}
          aria-label="Nouvelle liste"
        >
          +
        </button>
      </div>

      <button type="button" className="lists__settings" onClick={onOpenSettings} aria-label="Réglages">
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path
            fill="currentColor"
            d="M8 5.6a2.4 2.4 0 1 0 0 4.8 2.4 2.4 0 0 0 0-4.8m0 1.4a1 1 0 1 1 0 2 1 1 0 0 1 0-2"
          />
          <path
            fill="currentColor"
            d="m13.3 9.1.1-1.1-.1-1.1 1.2-.9-1.2-2-1.4.5a4.8 4.8 0 0 0-1.9-1.1L9.7 2H6.3L6 3.4a4.8 4.8 0 0 0-1.9 1.1l-1.4-.5-1.2 2 1.2.9-.1 1.1.1 1.1-1.2.9 1.2 2 1.4-.5c.5.5 1.2.9 1.9 1.1l.3 1.4h3.4l.3-1.4c.7-.2 1.4-.6 1.9-1.1l1.4.5 1.2-2zm-1.5-.2.1.2-.1 1.4.7.5-.2.3-.9-.3-1 .9c-.4.3-.8.5-1.3.6l-.4.1-.2 1h-.4l-.2-1-.4-.1c-.5-.1-.9-.3-1.3-.6l-1-.9-.9.3-.2-.3.7-.5-.1-1.4.1-1.4-.7-.5.2-.3.9.3 1-.9c.4-.3.8-.5 1.3-.6l.4-.1.2-1h.4l.2 1 .4.1c.5.1.9.3 1.3.6l1 .9.9-.3.2.3-.7.5z"
          />
        </svg>
      </button>

      {creating && (
        <form
          className="lists__create"
          onSubmit={(event) => {
            event.preventDefault();
            void createList(name);
            setName('');
            setCreating(false);
          }}
        >
          <input
            className="lists__input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Nom de la liste"
            aria-label="Nom de la liste"
            maxLength={80}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- champ ouvert à la demande
            autoFocus
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setName('');
                setCreating(false);
              }
            }}
          />
          <button type="submit" className="button button--small" disabled={name.trim().length === 0}>
            Créer
          </button>
          <button
            type="button"
            className="button button--small button--ghost"
            onClick={() => {
              setName('');
              setCreating(false);
            }}
          >
            Annuler
          </button>
        </form>
      )}
    </div>
  );
}
